import React, {useState, useMemo, useContext, useEffect} from 'react';
import styled from 'styled-components';

import Icon from 'components/icons/icon';

import SearchExistingFilter from 'components/pages/search/searchExistingFilter';
import SearchFilter from 'components/pages/search/searchFilter';
import SearchCheckboxes from 'components/pages/search/searchCheckboxes';
import SearchRange from 'components/pages/search/searchRange';

import theme, {bp, color} from 'settings/theme';

import {SearchContext} from 'services/searchService';


type Props = {
  toggleSidebar: () => void
};

type ExistingFilter = {
  name: string,
  value: string
};


// Component
const Component: React.FC<Props> = (props) => {

  const search = useContext(SearchContext);

  // Keyword Box
  const [keywordsInput, setKeywordsInput] = useState('');

  const handleKeywordsChange = (e) => {
    setKeywordsInput(e.target.value);
  };

  const handleKeywordsSubmit = (e) => {

    e.preventDefault();

    search.setParam!('keywords', keywordsInput);
    search.doSearch!();

    setKeywordsInput('');
    props.toggleSidebar();

  };

  // Calculate existing filters to show
  const existingFilters: ExistingFilter[] = useMemo(() => {
      
    // Turn the search params into an array
    let filters = Object.entries(search.params ?? {}).map(item => {
      return {name: item[0], value: item[1]};
    })

    // Remove any that need to be hidden
    const lockedFilters = search.lockedFilters ?? [];
    filters = filters.filter(item => lockedFilters.includes(item.name.toLowerCase()) == false);

    // Remove the page setting because we're not interested in it
    // filters = filters.filter(item => item.name !== 'page')

    return filters;

  }, [search.params]);


  // Calculate filters to show
  const filterParamTypes: SearchParamType[] = useMemo(() => {
    const lockedFilters = search.lockedFilters ?? []
    let newParamTypes = (search.paramTypes ?? []).filter(item => lockedFilters.includes(item.name.toLowerCase()) == false)
    return newParamTypes
  }, [search.paramTypes]);

    

  // Clear all filters
  const handleRemoveAllFilters = () => {
    search.clearParams!()
    search.doSearch!()
  }


  return (
    <StyledComponent className="searchSidebar">

      {/* Existing Filters */}
      <div className="searchSidebar-box searchSidebar-existing">

        <div className="searchSidebar-existing-top">

          <div className="searchSidebar-existing-icon">
            <Icon iconName="filter" />
          </div>
          <div className="searchSidebar-existing-title">
            <strong>{existingFilters.length}&nbsp;</strong> Filter{existingFilters.length !== 1 && <span>s</span>} Set
          </div>

          {existingFilters.length > 0 &&
            <div className="searchSidebar-existing-clear">
              <button onClick={handleRemoveAllFilters}>
                (<span>clear all</span>)
              </button>
            </div>
          }
            
        </div>

        {existingFilters.length > 0 &&
          <div className="searchSidebar-existing-filters">

            {existingFilters.length > 0 && existingFilters.map(item => (
              <SearchExistingFilter filter={item} key={item.name} />
            ))}

          </div>
        }
          
      </div>

      <div className="searchSidebar-box searchSidebar-keywords">
        <div className="searchSidebar-keywords-inner">
          <form onSubmit={handleKeywordsSubmit}>
            <input name="search" type="text" value={keywordsInput} onChange={handleKeywordsChange} placeholder="Search Products" />
            <button type="submit">
              <Icon iconName="magnifying-glass" />
            </button>
          </form>
        </div>
      </div>

      {filterParamTypes && filterParamTypes.length > 0 &&
        <>
          {filterParamTypes.map(paramType => (
            <SearchFilter title={paramType.filterTitle ?? ''} paramType={paramType} key={paramType.name}>
                
              {paramType.controlType === 'checkboxes' &&
                <SearchCheckboxes paramType={paramType} />
              }

              {paramType.controlType === 'range' &&
                <SearchRange paramType={paramType} />
              }

            </SearchFilter>
          ))}
        </>
      }


    </StyledComponent>
  );

}


export default Component;




// Styles
const StyledComponent = styled.div`

overflow-y: auto;

display: flex;
flex-direction: column;
width: 100%;
padding-bottom: 200px;
font-size: 14px;

button{
  font-size: 14px;
}

.searchSidebar-box{
  padding: 30px 20px;
  border-bottom: solid 1px ${color.lines};
}


.searchSidebar-existing{

  .searchSidebar-existing-top{
    display: flex;
    align-items: center;
    justify-content: stretch;
    font-size: 18px;
  }

  .searchSidebar-existing-icon{

    flex: 0 0 auto;

    .icon{

      width: 20px;
      height: 20px;

      svg{
        fill: ${color.primary};
      }

    }

  }

  .searchSidebar-existing-title{
    color: ${color.primary};
    flex: 0 0 auto;
    margin-left: 12px;
  }

  .searchSidebar-existing-clear{

    flex: 1 1 auto;
    text-align: right;

    button{

      color: ${color.grey(50)};
      font-size: 16px;

      span{
        text-decoration: underline;
      }

    }

  }

  .searchSidebar-existing-filters{

    margin-top: 24px;

    .searchExistingFilter:not(:first-child){
      margin-top: 5px;
    }

  }

}


  .searchSidebar-keywords{

    .searchSidebar-keywords-inner{
      position: relative;
      height: 36px;
    }

    form{
      height: 100%;
    }

    input{

      width: 100%;
      height: 100%;
      padding-left: 10px;
      color: ${color.grey(30)};
      font-size: 14px;
      border-radius: 4px;
      border: none;
      background-color: ${color.grey(97)};
      outline: none;

      &:focus{
        padding-left: 9px;
        outline: none;
        border: solid 1px rgba(0,157,224,1);
      }

    }

    button{

      position: absolute;
      right: 1px;
      top: 1px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border-radius: 4px;
      background-color: ${color.grey(95)};
      transition: background-color 0.15s ease-out;

      .icon{

        width: 16px;
        height: 16px;

        svg{
          fill: ${color.grey(30)};
        }

      }

      &:hover, &:focus{
        background-color: ${color.grey(90)};
      }

    }

  }


  @media (min-width: ${bp(4)}) {

    font-size: 16px;

    button{
      font-size: 16px;
    }

  }

  @media (min-width: ${bp(5)}) {

    .searchSidebar-box{
      padding: 30px;
      border-bottom: solid 1px ${color.lines};
    }

    .searchSidebar-existing{

      .searchSidebar-existing-filters{

        .searchExistingFilter:not(:last-child){
          margin-bottom: 8px;
        }

      }

    }

  }


`;



